import { FORM_VALUES } from './constants';
import { checkIsFlatCategory } from './common';
import phoneOperatorValidate from './phoneOperatorValidate';

const MAX_INT_VALUE = 2_147_483_647;
const CATEGORY_HOUSE = '5';
const CATEGORY_OFFICE = '7';
const CATEGORY_REQUIRING_ROOMS = ['1', '2', '3', '5', '7'];

const validateFloor = (value, values) =>
  Number(value) > 0 &&
  Number(value) <= Number(values[FORM_VALUES.totalFloors]) &&
  Number(value) <= MAX_INT_VALUE;

const validateNumber = value =>
  Number(value) > 0 && Number(value) <= MAX_INT_VALUE;

const validatePrice = value =>
  Number(value) >= 10 && Number(value) <= MAX_INT_VALUE;

const getFloorError = (values, t) => {
  if (Number(values[FORM_VALUES.floor]) < 1 || !values[FORM_VALUES.floor]) {
    return t('new_ad.above_zero');
  }
  if (Number(values[FORM_VALUES.floor]) > MAX_INT_VALUE) {
    return t('new_ad.less_than', { value: MAX_INT_VALUE + 1 });
  }
  if (values[FORM_VALUES.totalFloors]) {
    return t('new_ad.equal_or_less', {
      value: values[FORM_VALUES.totalFloors]
    });
  }
};

const getNumberError = field => (values, t) =>
  Number(values[field]) < 1 || !values[field]
    ? t('new_ad.above_zero')
    : t('new_ad.less_than', { value: MAX_INT_VALUE + 1 });

const getPriceError = (values, t) =>
  Number(values[FORM_VALUES.price]) < 10 || !values[FORM_VALUES.price]
    ? t('new_ad.above_ten')
    : t('new_ad.less_than', { value: MAX_INT_VALUE + 1 });

const getImagesError = (values, t) =>
  values[FORM_VALUES.images]?.length < 4 || !values[FORM_VALUES.images]
    ? t('new_ad.min_photos')
    : t('new_ad.max_photos');

const getPhoneError = (values, t) =>
  values[FORM_VALUES.phone]?.length > 2 &&
  !phoneOperatorValidate(values[FORM_VALUES.phone])
    ? t('new_ad.wrong_operator')
    : t('new_ad.wrong_phone');

export const validatorsConfig = {
  [FORM_VALUES.locationId]: {
    required: (_, cityHasLocations) => cityHasLocations,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.cityId]: {
    required: true,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.address]: {
    required: true,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.coordinates]: {
    required: true,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.paidDaily]: {
    required: values => values[FORM_VALUES.leased] === 'true',
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.buildingType]: {
    required: values => values[FORM_VALUES.categoryId] === CATEGORY_OFFICE,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.roomsAmount]: {
    required: values =>
      CATEGORY_REQUIRING_ROOMS.includes(values[FORM_VALUES.categoryId]),
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.area]: {
    required: true,
    validator: validateNumber,
    errorMessage: getNumberError(FORM_VALUES.area)
  },
  [FORM_VALUES.landArea]: {
    required: values => values[FORM_VALUES.categoryId] === CATEGORY_HOUSE,
    validator: validateNumber,
    errorMessage: getNumberError(FORM_VALUES.landArea)
  },
  [FORM_VALUES.floor]: {
    required: values => checkIsFlatCategory(values[FORM_VALUES.categoryId]),
    validator: validateFloor,
    errorMessage: getFloorError
  },
  [FORM_VALUES.totalFloors]: {
    required: values => checkIsFlatCategory(values[FORM_VALUES.categoryId]),
    validator: validateNumber,
    errorMessage: getNumberError(FORM_VALUES.totalFloors)
  },
  [FORM_VALUES.price]: {
    required: true,
    validator: validatePrice,
    errorMessage: getPriceError
  },
  [FORM_VALUES.name]: {
    required: true,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.email]: {
    required: true,
    validator: value => value?.includes('@') && value?.includes('.'),
    errorMessage: 'new_ad.wrong_email'
  },
  [FORM_VALUES.phone]: {
    required: true,
    validator: value => value?.length >= 10 && phoneOperatorValidate(value),
    errorMessage: getPhoneError
  },
  [FORM_VALUES.categoryId]: {
    required: true,
    errorMessage: 'new_ad.should_exist'
  },
  [FORM_VALUES.images]: {
    required: true,
    validator: value => value?.length >= 4 && value?.length <= 30,
    errorMessage: getImagesError
  }
};

export const validateFunc = (cityHasLocations, t) => values =>
  Object.entries(validatorsConfig).reduce((errors, [fieldName, config]) => {
    const { required, validator, errorMessage } = config;
    const isRequired =
      typeof required === 'function'
        ? required(values, cityHasLocations)
        : required;
    const fieldValue = values[fieldName];
    if (
      isRequired &&
      (!fieldValue || (validator && !validator(fieldValue, values)))
    ) {
      // eslint-disable-next-line no-param-reassign
      errors[fieldName] =
        typeof errorMessage === 'function'
          ? errorMessage(values, t)
          : t(errorMessage);
    }
    return errors;
  }, {});
